import { getRoles, resetUserPassword, toggleUserDisabled, updateUser } from "@/services/user-service";
import { UserAccessLevel, useUserStore } from "@/stores/user-store";
import {
	ExclamationCircleOutlined,
	KeyOutlined,
	LockOutlined,
	UnlockOutlined,
} from "@ant-design/icons";
import {
	Button,
	Divider,
	Form,
	Input,
	Modal,
	Select,
	Space,
	Spin,
	Typography,
	message,
} from "antd";
import { type FC, useCallback, useEffect, useState } from "react";

const { Text, Paragraph } = Typography;
const { confirm } = Modal;

interface EditUserModalProps {
	open: boolean;
	user: any;
	onCancel: () => void;
	onSuccess: () => void;
}

const EditUserModal: FC<EditUserModalProps> = ({ open, user: editingUser, onCancel, onSuccess }) => {
	const [form] = Form.useForm();
	const currentUser = useUserStore((state) => state.user);
	const [roles, setRoles] = useState<any[]>([]);
	const [rolesLoading, setRolesLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [resettingPassword, setResettingPassword] = useState(false);
	const [togglingDisable, setTogglingDisable] = useState(false);

	const currentAccessLevel = currentUser?.role?.accessLevel ?? currentUser?.role?.access_level ?? 0;
	const isSuperAdmin = currentAccessLevel >= UserAccessLevel.SUPER_ADMIN;
	const isAdmin = currentAccessLevel >= UserAccessLevel.ADMIN;
	const isSelf = editingUser?.id === currentUser?.id;

	const isAccountDisabled = editingUser?.isAccountDisabled ?? editingUser?.is_account_disabled ?? false;

	const fetchRoles = useCallback(async () => {
		try {
			setRolesLoading(true);
			const rolesList = await getRoles();
			setRoles(rolesList);
		} catch (error) {
			console.error("Failed to fetch roles:", error);
		} finally {
			setRolesLoading(false);
		}
	}, []);

	useEffect(() => {
		if (open && editingUser) {
			fetchRoles();
			form.setFieldsValue({
				name: editingUser.name,
				email: editingUser.email,
				phone_number: editingUser.phoneNumber ?? editingUser.phone_number ?? "",
				role_id: editingUser.role?.id ?? editingUser.roleId ?? editingUser.role_id,
			});
		}
	}, [open, editingUser, form, fetchRoles]);

	const handleSave = async () => {
		try {
			const values = await form.validateFields();
			setSaving(true);

			await updateUser(editingUser.id, {
				name: values.name,
				role_id: values.role_id,
				phone_number: values.phone_number || "",
			});

			message.success("User updated successfully");
			onSuccess();
		} catch (error: any) {
			if (error.errorFields) return; // Form validation error
			message.error(error.message || "Failed to update user");
		} finally {
			setSaving(false);
		}
	};

	const handleResetPassword = () => {
		confirm({
			title: "Send Password Reset",
			icon: <ExclamationCircleOutlined />,
			content: (
				<div>
					<Paragraph>
						This will generate a password reset link for <Text strong>{editingUser?.email}</Text>.
					</Paragraph>
					<Paragraph type="secondary">
						You can copy the link and share it with the user.
					</Paragraph>
				</div>
			),
			okText: "Generate Link",
			okButtonProps: { style: { backgroundColor: '#92278F', borderColor: '#92278F' } },
			onOk: async () => {
				try {
					setResettingPassword(true);
					const result = await resetUserPassword(editingUser.id);

					Modal.success({
						title: "Password Reset Link Generated",
						content: (
							<div>
								<Paragraph>Reset link for <Text strong>{result.email}</Text>:</Paragraph>
								<Input.TextArea
									value={result.resetLink}
									autoSize={{ minRows: 2, maxRows: 4 }}
									readOnly
									onClick={(e) => {
										(e.target as HTMLTextAreaElement).select();
										navigator.clipboard.writeText(result.resetLink);
										message.success("Link copied to clipboard!");
									}}
									style={{ cursor: "pointer" }}
								/>
								<Paragraph type="secondary" style={{ marginTop: 8 }}>
									Click the link above to copy it.
								</Paragraph>
							</div>
						),
						width: 520,
					});
				} catch (error: any) {
					message.error(error.message || "Failed to generate password reset link");
				} finally {
					setResettingPassword(false);
				}
			},
		});
	};

	const handleToggleDisable = () => {
		const action = isAccountDisabled ? "enable" : "disable";
		confirm({
			title: `${isAccountDisabled ? "Enable" : "Disable"} User Account`,
			icon: <ExclamationCircleOutlined />,
			content: (
				<Paragraph>
					Are you sure you want to <Text strong>{action}</Text> the account for{" "}
					<Text strong>{editingUser?.name}</Text> ({editingUser?.email})?
					{!isAccountDisabled && (
						<Text type="secondary" style={{ display: "block", marginTop: 8 }}>
							The user will not be able to log in while their account is disabled.
						</Text>
					)}
				</Paragraph>
			),
			okText: isAccountDisabled ? "Enable Account" : "Disable Account",
			okButtonProps: {
				danger: !isAccountDisabled,
				style: isAccountDisabled ? { backgroundColor: '#92278F', borderColor: '#92278F' } : undefined,
			},
			onOk: async () => {
				try {
					setTogglingDisable(true);
					await toggleUserDisabled(editingUser.id, !isAccountDisabled);
					message.success(`User account ${action}d successfully`);
					onSuccess();
				} catch (error: any) {
					message.error(error.message || `Failed to ${action} user account`);
				} finally {
					setTogglingDisable(false);
				}
			},
		});
	};

	if (!editingUser) return null;

	return (
		<Modal
			title="Edit User"
			open={open}
			onCancel={onCancel}
			footer={[
				<Button key="cancel" onClick={onCancel}>
					Cancel
				</Button>,
				<Button
					key="save"
					type="primary"
					onClick={handleSave}
					loading={saving}
					style={{ backgroundColor: '#92278F', borderColor: '#92278F' }}
				>
					Save Changes
				</Button>,
			]}
			width={520}
			destroyOnClose
		>
			<Spin spinning={rolesLoading}>
				<Form form={form} layout="vertical" style={{ marginTop: 16 }}>
					<Form.Item
						label="Name"
						name="name"
						rules={[{ required: true, message: "Name is required" }]}
					>
						<Input placeholder="Enter name" />
					</Form.Item>

					<Form.Item label="Email" name="email">
						<Input disabled />
					</Form.Item>

					<Form.Item label="Phone Number" name="phone_number">
						<Input placeholder="Enter phone number" />
					</Form.Item>

					<Form.Item
						label="Role"
						name="role_id"
						rules={[{ required: true, message: "Role is required" }]}
					>
						<Select placeholder="Select a role" disabled={isSelf}>
							{roles.map((role: any) => (
								<Select.Option key={role.id} value={role.id}>
									{role.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</Form>

				{isAdmin && !isSelf && (
					<>
						<Divider style={{ marginTop: 8, marginBottom: 16 }} />
						<Text strong style={{ display: "block", marginBottom: 12 }}>
							Account Actions
						</Text>
						<Space direction="vertical" style={{ width: "100%" }} size="small">
							<Button
								icon={<KeyOutlined />}
								onClick={handleResetPassword}
								loading={resettingPassword}
								block
								style={{ textAlign: "left" }}
							>
								Generate Password Reset Link
							</Button>

							<Button
								icon={isAccountDisabled ? <UnlockOutlined /> : <LockOutlined />}
								onClick={handleToggleDisable}
								loading={togglingDisable}
								danger={!isAccountDisabled}
								block
								style={{ textAlign: "left" }}
							>
								{isAccountDisabled ? "Enable Account" : "Disable Account"}
							</Button>
						</Space>
					</>
				)}
			</Spin>
		</Modal>
	);
};

export { EditUserModal };
